import { useMutation } from '@apollo/client';
import { useScrollLock } from '@energiebespaarders/hooks';
import { Accordion, Box, theme } from '@energiebespaarders/symbols';
import { Book, Medium } from '@energiebespaarders/symbols/helpers';
import mediaMin from '@energiebespaarders/symbols/styles/breakpoints';
import { padding } from '@energiebespaarders/symbols/styles/mixins';
import { ArrowElbowDownRight, CaretDown, CaretUp, User, UserCircle } from '@phosphor-icons/react';
import { useRouter } from 'next/router';
import { rgba } from 'polished';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import useIsAuthenticated from '~/hooks/useIsAuthenticated';
import { useMeOptional } from '~/hooks/useMe';
import { enumValues } from 'src/typeHelpers';
import { useClearSession } from '../../hooks/useClearSession';
import { LOGOUT_CUSTOMER } from '../../queries/customer';
import { shade } from '../../styles/mixins';
import HybridLink from '../HybridLink';
import SocialLinks from '../SocialLinks';
import { legacyNavMenuLinks, MainNavItem, mainNavTitles, navMenuLinks } from './constants';
import NavActionButton from './NavActionButton';
import type { logoutCustomer } from '../../types/generated/logoutCustomer';

const StyledSideNav = styled.nav<{ $isOpen: boolean }>`
  position: fixed;
  top: ${x => x.theme.fixedSizes.navHeight};
  right: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding-bottom: ${x => x.theme.fixedSizes.navHeight};
  overflow-y: auto;
  background: ${x => x.theme.colors.greenSlate};
  transition: transform 0.3s
    ${x => (x.$isOpen ? x.theme.curves.decelerate : x.theme.curves.accelerate)};
  transform: translateX(100%);
  z-index: ${x => x.theme.z.navMask + 100};
  box-shadow:
    0 2px 6px -2px ${x => rgba(shade(0.8, x.theme.colors.green), 0.2)},
    0 3px 4px 0 rgba(0, 0, 0, 0.1);

  ${mediaMin.md} {
    width: 75%;
    left: auto;
  }

  ${x =>
    x.$isOpen &&
    css`
      transform: translateX(0);
    `};
`;

const StyledNavMask = styled.div<{ $isOpen: boolean; onClick: any }>`
  position: fixed;
  top: ${x => x.theme.fixedSizes.navHeight};
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: black;
  transition: opacity 0.5s;
  opacity: 0;
  z-index: ${x => x.theme.z.navMask};
  pointer-events: none;

  ${x =>
    x.$isOpen &&
    css`
      opacity: 0.75;
      cursor: pointer;
      pointer-events: all;
    `};
`;

const StyledSideHeader = styled(Box)`
  background: white;
  font-weight: 500;
`;

const StyledSideNavLink = styled(HybridLink)`
  ${padding(2)};
  ${padding(3, 'l')};
  display: block;
  color: ${x => x.theme.colors.greenDarker};
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: ${x => x.theme.colors.green};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StyledSideNavSubItem = styled(Box)<{ $isActive: boolean }>`
  font-weight: 500;
  cursor: pointer;
  color: ${x => x.theme.colors.greenDark};
`;

const MobileNavFooter = styled(Box)`
  background: ${x => x.theme.colors.greenSlate};
  font-size: ${x => x.theme.type.scale[6]};
`;

const MobileNavFooterLink = styled(HybridLink)`
  ${padding(2, 'x')};
  ${padding(1, 'y')};
  display: block;
  color: ${x => x.theme.colors.greenDarker};

  &:hover {
    color: ${x => x.theme.colors.green};
  }
`;

interface MobileNavProps {
  isOpen: boolean;
  toggleSidebarNav: () => void;
}

export const MobileNav: React.FC<MobileNavProps> = ({ isOpen, toggleSidebarNav }) => {
  const { me } = useMeOptional();
  const router = useRouter();
  const isAuthenticated = useIsAuthenticated();
  const [activeMenu, setActiveMenu] = useState<MainNavItem | ''>('');
  const [activeSubMenu, setActiveSubMenu] = useState<string>('');

  const { clearSession } = useClearSession();
  const [logout] = useMutation<logoutCustomer>(LOGOUT_CUSTOMER, {
    onCompleted: async data => data.logoutCustomer && clearSession(),
  });

  const MainNavItemBase = ({ mainItem }: { mainItem: MainNavItem }) => (
    <StyledSideHeader p={2} style={{ cursor: 'pointer' }}>
      {mainNavTitles[mainItem]}
    </StyledSideHeader>
  );

  useScrollLock(isOpen);

  const toggleActiveSubMenu = (key: string) => {
    setActiveSubMenu(activeSubMenu === key ? '' : key);
  };

  const goToLink = (link: string) => {
    toggleSidebarNav();
    void router.push(link);
  };

  const useLegacyAccount = me?.__typename === 'Customer' && me.isLegacyCustomer;
  const navLinks = useLegacyAccount ? legacyNavMenuLinks : navMenuLinks;

  return (
    <>
      <StyledNavMask $isOpen={isOpen} onClick={isOpen ? toggleSidebarNav : null} />
      <StyledSideNav role="navigation" aria-label="Mobiele navigatie" $isOpen={isOpen}>
        {enumValues(MainNavItem)
          .sort(a => (a === MainNavItem.Account ? -1 : 1))
          .filter(mainItem =>
            isAuthenticated.asCustomer ? true : mainItem !== MainNavItem.Account,
          )
          .map((mainItem, mainIndex) => (
            <Box key={`side-${mainItem}-${mainIndex}`}>
              <Accordion
                isOpened={mainItem === activeMenu}
                override
                defaultOpen={false}
                contentBgColor="white"
                contentPadding={0}
                basePadding={0}
                customBase={<MainNavItemBase mainItem={mainItem} />}
                onClick={() => setActiveMenu(activeMenu === mainItem ? '' : mainItem)}
                style={{ borderBottom: '1px solid', borderColor: theme.colors.grayLight }}
              >
                {navLinks[mainItem].map((links, subIndex) => (
                  <React.Fragment key={`navLinks-${subIndex}`}>
                    {links
                      .filter(link => link.isHeading)
                      .map(link => (
                        <Box
                          key={`heading-${link.to}`}
                          px={2}
                          pt={2}
                          onClick={() =>
                            link.to && activeSubMenu === `${mainIndex}-${subIndex}`
                              ? goToLink(link.to)
                              : toggleActiveSubMenu(`${mainIndex}-${subIndex}`)
                          }
                        >
                          <StyledSideNavSubItem
                            pb={2}
                            $isActive={activeSubMenu === `${mainIndex}-${subIndex}`}
                          >
                            {link.label}{' '}
                            {activeSubMenu === `${mainIndex}-${subIndex}` ? (
                              <CaretUp
                                weight="fill"
                                size="0.66em"
                                style={{ verticalAlign: 'baseline' }}
                              />
                            ) : (
                              <CaretDown
                                weight="fill"
                                size="0.66em"
                                style={{ verticalAlign: 'baseline' }}
                              />
                            )}
                          </StyledSideNavSubItem>
                        </Box>
                      ))}

                    {links
                      .filter(
                        link =>
                          !!link.to &&
                          !link.isHeading &&
                          (activeSubMenu === `${mainIndex}-${subIndex}` ||
                            activeMenu === MainNavItem.Account),
                      )
                      .map(link => (
                        <StyledSideNavLink
                          key={`link-${link.to}`}
                          to={link.to}
                          onClick={toggleSidebarNav}
                        >
                          <ArrowElbowDownRight
                            weight="thin"
                            style={{ verticalAlign: 'text-top' }}
                          />{' '}
                          {link.label}
                        </StyledSideNavLink>
                      ))}
                  </React.Fragment>
                ))}
              </Accordion>
            </Box>
          ))}

        <Box p={2}>
          <NavActionButton fluid onClick={toggleSidebarNav} />
        </Box>

        <MobileNavFooter>
          {me?.__typename === 'Customer' ? (
            <>
              <MobileNavFooterLink to="/mijn-gegevens">
                <User weight="fill" /> {me.firstName} {me.lastName}
              </MobileNavFooterLink>
              <MobileNavFooterLink
                onClick={() => {
                  void logout();
                  toggleSidebarNav();
                }}
              >
                <Book>Uitloggen</Book>
              </MobileNavFooterLink>
            </>
          ) : (
            <>
              <Box px={2}>
                <UserCircle weight="duotone" /> <Medium>Inloggen</Medium>
              </Box>
              <MobileNavFooterLink to="/login" onClick={toggleSidebarNav}>
                Woningeigenaar
              </MobileNavFooterLink>
              <MobileNavFooterLink to="/samen" onClick={toggleSidebarNav}>
                Intermediair
              </MobileNavFooterLink>
              <MobileNavFooterLink
                href="https://partner.energiebespaarders.nl"
                onClick={toggleSidebarNav}
              >
                Installateur
              </MobileNavFooterLink>
            </>
          )}
        </MobileNavFooter>
        <Box px={2} py={3}>
          <SocialLinks justifyContent="flex-start" />
        </Box>
      </StyledSideNav>
    </>
  );
};

export default MobileNav;
